import { useState } from 'react'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { ContentCopy, Check } from '@mui/icons-material'

import { trackUi } from '@/app/selfAnalytics'

interface CodeBlockProps {
    code: string
    language?: string
    /** Optional title shown above the code block */
    title?: string
    /** Where the snippet lives, for the Y-APP `snippet_copied` event. Default 'docs'. */
    placement?: string
}

export function CodeBlock({ code, language, title, placement = 'docs' }: CodeBlockProps) {
    const [copied, setCopied] = useState(false)

    const handleCopy = async () => {
        await navigator.clipboard.writeText(code)
        trackUi('snippet_copied', { framework: language ?? 'text', placement })
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    return (
        <Box sx={{ position: 'relative', my: 2 }}>
            {title && (
                <Box
                    sx={{
                        px: 2,
                        py: 0.75,
                        bgcolor: '#1e293b',
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{ color: '#94a3b8', fontFamily: 'monospace', fontWeight: 600 }}
                    >
                        {title}
                    </Typography>
                    {language && (
                        <Typography
                            variant="caption"
                            sx={{ color: '#64748b', fontFamily: 'monospace' }}
                        >
                            {language}
                        </Typography>
                    )}
                </Box>
            )}
            <Box
                sx={{
                    position: 'relative',
                    bgcolor: '#0f172a',
                    p: 2,
                    borderRadius: title ? '0 0 8px 8px' : 2,
                    overflow: 'auto',
                    '&:hover .copy-btn': { opacity: 1 },
                }}
            >
                <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
                    <IconButton
                        className="copy-btn"
                        size="small"
                        onClick={handleCopy}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            opacity: 0.5,
                            transition: 'opacity 0.2s',
                            color: '#94a3b8',
                            bgcolor: 'rgba(255,255,255,0.05)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                        }}
                    >
                        {copied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                    </IconButton>
                </Tooltip>
                <Typography
                    component="pre"
                    sx={{
                        m: 0,
                        fontFamily: '"Fira Code", "JetBrains Mono", "SF Mono", Consolas, monospace',
                        fontSize: 13,
                        lineHeight: 1.6,
                        color: '#e2e8f0',
                        whiteSpace: 'pre',
                        overflowX: 'auto',
                    }}
                >
                    {code}
                </Typography>
            </Box>
        </Box>
    )
}
